import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject, takeUntil} from 'rxjs';
import {FuseMediaWatcherService} from '@fuse/services/media-watcher';
import {FuseNavigationService, FuseVerticalNavigationComponent} from '@fuse/components/navigation';
import {Navigation} from 'app/core/navigation/navigation.types';
import {NavigationService} from 'app/core/navigation/navigation.service';
import {User} from '@models/user.interface';
import {UserService} from 'app/core/user/user.service';
import {environment} from '@env/environment';
import {HotelService} from '@services/hotel.service';
import {Contractee} from '@models/contractee.model';
import {ProviderService} from '@services/provider.service';
import {Provider} from '@hotels/providers/contacts.types';
import {AccountRole} from '@enums/AccountRole';
import {NotificationsService} from '../../../common/notifications/notifications.service';
import {Notification} from '../../../common/notifications/notifications.types';
import {PolizaService} from '@services/poliza.service';
import {GenericSelect} from '@models/generic-select.interface';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    styleUrls: ['./classy.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean;
    desktopNavigation: 'full' | 'icons' | 'hidden' = 'full';
    mobileNavigationOpened = false;

    get navigationOpened(): boolean {
        return this.isScreenSmall ? this.mobileNavigationOpened : this.desktopNavigation !== 'hidden';
    }

    get navigationControlLabel(): string {
        if (this.isScreenSmall) return this.mobileNavigationOpened ? 'Ocultar menú' : 'Mostrar menú';
        return this.desktopNavigation === 'full' ? 'Mostrar solo iconos' :
            this.desktopNavigation === 'icons' ? 'Ocultar menú' : 'Mostrar menú completo';
    }

    get navigationControlIcon(): string {
        if (this.isScreenSmall) return this.mobileNavigationOpened ? 'x' : 'menu';
        return this.desktopNavigation === 'full' ? 'view-list' :
            this.desktopNavigation === 'icons' ? 'dots-vertical' : 'menu-alt-2';
    }

    onNavigationOpened(opened: boolean): void {
        // Native backdrop and route closures belong to the mobile overlay, not the desktop choice.
        if (this.isScreenSmall) this.mobileNavigationOpened = opened;
    }
    navigation: Navigation;
    user: User;
    hotel: Contractee;
    provider: Provider;
    versionSISE = environment.versionSISE;
    alertNotifications: Notification[] = [];
    messagesNotifications: Notification[] = [];
    unreadAlertCount: number = 0;
    unreadMessageCount: number = 0;
    private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

// -----------------------------------------------------------------------------------------------------
// @ Accessors
// -----------------------------------------------------------------------------------------------------

    /**
     * Constructor
     */
    constructor(
        private readonly _activatedRoute: ActivatedRoute,
        private readonly _router: Router,
        private readonly _navigationService: NavigationService,
        private readonly _userService: UserService,
        private readonly _fuseMediaWatcherService: FuseMediaWatcherService,
        private readonly _fuseNavigationService: FuseNavigationService,
        private readonly _hotelService: HotelService,
        private readonly _providerService: ProviderService,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _notificationsService: NotificationsService,
        private readonly _polizaService: PolizaService,
    ) {
    }

// -----------------------------------------------------------------------------------------------------
// @ Lifecycle hooks
// -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    get isProvider(): boolean {
        return this.user.rol === AccountRole.provider;
    }

// -----------------------------------------------------------------------------------------------------
// @ Public methods
// -----------------------------------------------------------------------------------------------------

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (!navigation) return;
        if (name !== 'mainNavigation' || this.isScreenSmall) {
            navigation.toggle();
            return;
        }
        this.desktopNavigation = this.desktopNavigation === 'full' ? 'icons' :
            this.desktopNavigation === 'icons' ? 'hidden' : 'full';
    }

    /**
     * On init
     */
    ngOnInit(): void {
        // Bootstrap del proveedor: determinar visibilidad del menú Pólizas
        const storedUser = JSON.parse(localStorage.getItem('user') ?? 'null');
        if (storedUser?.rol === AccountRole.provider) {
            localStorage.setItem('polizasMenuEnabled', 'false');
            this._polizaService.getContratantesDisponibles()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (list: GenericSelect[]): void => {
                        localStorage.setItem('polizasMenuEnabled', list.length > 0 ? 'true' : 'false');
                        this._navigationService.get().pipe(takeUntil(this._unsubscribeAll)).subscribe();
                    },
                    error: (): void => {
                        // polizasMenuEnabled ya es 'false' — menú oculto por defecto
                    }
                });
        } else if (storedUser?.rol === AccountRole.semiMaster) {
            localStorage.setItem('polizasSemiMasterEnabled', 'false');
            this._polizaService.getContratantesDisponibles()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (list: GenericSelect[]): void => {
                        localStorage.setItem('polizasSemiMasterEnabled', list.length > 0 ? 'true' : 'false');
                        this._navigationService.get().pipe(takeUntil(this._unsubscribeAll)).subscribe();
                    },
                    error: (): void => {
                        // polizasSemiMasterEnabled ya es 'false' — menú oculto por defecto
                    }
                });
        }

        // Bootstrap de la compañía: visibilidad de los menús Pólizas y Fianzas según sus feature flags
        if (storedUser?.rol === AccountRole.adminHotel || storedUser?.rol === AccountRole.accountant) {
            localStorage.setItem('companyPolizasMenuEnabled', 'false');
            localStorage.setItem('companyFianzasMenuEnabled', 'false');
            this._hotelService.getCurrent()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (hotel): void => {
                        localStorage.setItem('companyPolizasMenuEnabled', hotel?.isPolizas === true ? 'true' : 'false');
                        localStorage.setItem('companyFianzasMenuEnabled', hotel?.isFianzas === true ? 'true' : 'false');
                        this._navigationService.get().pipe(takeUntil(this._unsubscribeAll)).subscribe();
                    },
                    error: (): void => {
                        // companyPolizasMenuEnabled / companyFianzasMenuEnabled ya son 'false' — menús ocultos por defecto
                    }
                });
        }

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Subscribe to the user service
        this._hotelService.hotel$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((hotel: Contractee) => {
                this.hotel = hotel;
            });


        // Subscribe to the provider Service
        this._providerService.provider$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((provider: Provider) => {
                this.provider = provider;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                const isScreenSmall = !matchingAliases.includes('md');
                if (this.isScreenSmall !== isScreenSmall) this.mobileNavigationOpened = false;
                this.isScreenSmall = isScreenSmall;
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to notification changes
        this._notificationsService.notifications$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: Notification[]) => {

                // Filter alert & messages notifications
                this._filterNotifications(notifications);

                // Calculate the unread count
                this.unreadMessageCount = this._calculateUnreadCount(this.messagesNotifications);
                this.unreadAlertCount = this._calculateUnreadCount(this.alertNotifications);

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        this._changeDetectorRef.markForCheck();
    }


    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private _filterNotifications(notifications: Notification[]): void {
        if (!notifications) return;
        if (this.user.rol === AccountRole.provider)
            this.alertNotifications = notifications.filter(notification => notification.type === 0).reverse();
        else {
            this.messagesNotifications = notifications.filter(n => !n.notificacion.startsWith('El proveedor')).reverse();
            this.alertNotifications = notifications.filter(n => n.notificacion.startsWith('El proveedor')).reverse();
        }
    }

    /**
     * Calculate the unread count
     *
     * @private
     */
    private _calculateUnreadCount(notifications: Notification[]): number {
        let count = 0;

        if (notifications?.length > 0)
            count = notifications.filter(notification => !notification.read).length;


        return count;
    }
}
